import { FocusEvent, useEffect, useState } from 'react';
import { FormikErrors } from 'formik';
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { Logger } from '@utils/logger';
import { AXNUIContentCustomAttributeField, SelectOption } from '@iddataweb/idw-common-ui/types';
import { AXNUISelectInputWithErrorMessage } from '@iddataweb/idw-common-ui/containers';
import { AttributeTypeField } from '@idw-types/data';

type CustomDropdownFieldProps = {
    attributeFieldContent: AXNUIContentCustomAttributeField;
    attributeFieldOptions: Array<SelectOption>;
    attribute: AttributeFormState;
    attributeTypeField: AttributeTypeField;
    fieldTouched: boolean;
    fieldError: string;
    fieldIsInvalid: boolean;
    fieldShouldBeHighlighted: boolean;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    onSetSubmitValue: (data: { inputData: { [key: string]: string } }) => void;
    isSubmitting: boolean;
};

export const CustomDropdownField = (props: CustomDropdownFieldProps) => {
    const {
        attributeFieldContent,
        attributeFieldOptions,
        attribute,
        attributeTypeField,
        fieldTouched,
        fieldError,
        fieldIsInvalid,
        fieldShouldBeHighlighted,
        setFieldValue,
        setFieldTouched,
        onBlur,
        onSetSubmitValue,
        isSubmitting
    } = props;

    const [selectedOption, setSelectedOption] = useState(null);

    useEffect(() => {
        let valueToUse: string = null;
        if (attribute.isPlaceholder && attribute.prefillValueMap[attributeTypeField.name]) {
            valueToUse = attribute.prefillValueMap[attributeTypeField.name];
        } else if (attribute.inputData[attributeTypeField.name]) {
            valueToUse = attribute.inputData[attributeTypeField.name];
        }

        const option = attributeFieldOptions.find(option => option.value === valueToUse) || null;
        setSelectedOption(option);
    }, [
        attribute.isPlaceholder,
        attribute.prefillValueMap,
        attribute.inputData[attributeTypeField.name],
        attributeFieldOptions
    ]);

    const handleChange = async (newValue: SelectOption) => {
        Logger.info(`[CustomDropdown] Selected Option: `, newValue);

        const value = newValue?.value || "";
        onSetSubmitValue({ inputData: { [attributeTypeField.name]: value } });
        await setFieldValue(attributeTypeField.name, value);
        setFieldTouched(attributeTypeField.name, true, true);
    };

    return (
        <AXNUISelectInputWithErrorMessage
            controlId={`attr-${attribute.id}-${attributeTypeField.name}-select-input`}
            automationId={`attr-${attribute.id}-${attributeTypeField.name}-select-input`}
            ariaLabel={""}
            isClearable={attribute.optional}
            isSearchable={false}
            title={attributeFieldContent.placeholder}
            placeholder={attributeFieldContent.placeholder}
            selectedOption={selectedOption}
            options={attributeFieldOptions}
            fieldName={attributeTypeField.name}
            fieldTouched={fieldTouched}
            fieldError={fieldError}
            fieldIsInvalid={fieldIsInvalid}
            fieldShouldBeHighlighted={fieldShouldBeHighlighted}
            onBlur={onBlur}
            setFieldTouched={setFieldTouched}
            onChange={handleChange}
            isDisabled={attribute.isDisabled}
			isPlaceholder={attribute.isPlaceholder}
			isOptional={attribute.optional}
            isSubmitting={isSubmitting}
        />
    );
};